import { Box } from '@material-ui/core';
import { DeclaredBusinessType, Merchant } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';

import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { IconHeader } from '@/components/shared/components/IconHeader';
import { AutocompleteSelectFormField } from '@/components/shared/components/NewContextForms/AutocompleteSelectFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { AcquisitionChannelOptions } from '@/constants/AcquisitionChannelOptions';
import { AmountOfEmployeesOptions } from '@/constants/AmountOfEmployeesOptions';
import { DeclaredBusinessTypeOptions } from '@/constants/DeclaredBusinessTypeOptions';
import { MerchantCategoryOptions } from '@/constants/MerchantCategoryOptions';
import { TurnoverOptions } from '@/constants/TurnoverOptions';
import { useScreen } from '@/hooks/useScreen';
import { getIconByMerchantVertical } from '@/utils/getIconByMerchantVertical';
import { getMerchantVerticalByMerchantCategory } from '@/utils/getMerchantVerticalByMerchantCategory';

interface MerchantRegistrationDataFormProps extends FormGridOptions {
  merchant: Merchant;
  showInternalData: boolean;
}

export const MerchantRegistrationDataForm = ({
  merchant,
  showInternalData,
  fullWidth,
  fieldVariant,
}: MerchantRegistrationDataFormProps) => {
  const { isMobile } = useScreen();
  const { getTextIdTranslation: t } = useTranslation();

  return (
    <FormGridContainer
      numberOfFieldsInFirstColumn={showInternalData ? 9 : 2}
      fullWidth={fullWidth}
      fieldVariant={fieldVariant}
    >
      {showInternalData && fieldVariant !== FieldVariant.form && (
        <>
          <IconHeader
            color={'secondary'}
            labelTid={'COMPANY_INFORMATION'}
            iconName={getIconByMerchantVertical(
              getMerchantVerticalByMerchantCategory(merchant.registrationFields.category),
            )}
            copyToClipboard={{ value: merchant.id, textId: 'COPY_MERCHANT_ID' }}
          />
          <Box mt={3} />
        </>
      )}
      <TextFormField
        label={'BUSINESS_NAME_FISCAL'}
        value={merchant.registrationFields.businessName}
        name={'businessName'}
      />
      <TextFormField
        label={
          (useFormContext()?.watch('declaredBusinessType') === DeclaredBusinessType.LTD &&
            fieldVariant === FieldVariant.form) ||
          (merchant.registrationFields.businessType.declaredBusinessType === DeclaredBusinessType.LTD &&
            fieldVariant === FieldVariant.dataRow)
            ? 'CIF'
            : 'DOCUMENT_ID_NUMBER'
        }
        value={merchant.registrationFields.corporateIdNumber}
        name={'corporateIdNumber'}
      />
      <TextFormField label={'POSTAL_CODE'} value={merchant.registrationFields.postalCode} name={'postalCode'} />
      <AutocompleteSelectFormField
        label={'MERCHANT_CATEGORY'}
        value={merchant.registrationFields.category}
        name={'category'}
        selectOptions={MerchantCategoryOptions(t)}
      />
      {showInternalData && (
        <SelectFormField
          label={'BUSINESS_TYPE'}
          value={merchant.registrationFields.businessType.declaredBusinessType}
          name={'declaredBusinessType'}
          selectOptions={DeclaredBusinessTypeOptions}
        />
      )}
      {showInternalData && (
        <SelectFormField
          label={'AMOUNT_OF_EMPLOYEES'}
          value={<Text tId={merchant.registrationFields.amountOfEmployees} />}
          name={'amountOfEmployees'}
          selectOptions={AmountOfEmployeesOptions}
        />
      )}
      {showInternalData && (
        <SelectFormField
          label={'TURNOVER'}
          value={merchant.registrationFields.turnover}
          name={'turnover'}
          selectOptions={TurnoverOptions}
        />
      )}
      {showInternalData && (
        <SelectFormField
          label={'ACQUISITION_CHANNEL'}
          value={merchant.acquisitionChannel}
          name={'acquisitionChannel'}
          selectOptions={AcquisitionChannelOptions}
        />
      )}
      {showInternalData && fieldVariant !== FieldVariant.form && (
        <Box mb={3} mt={isMobile ? 3 : 0}>
          <IconHeader
            color={'secondary'}
            labelTid={'CONTACT_PERSON_DETAILS'}
            iconName={'person'}
            copyToClipboard={{ value: merchant.id, textId: 'COPY_MERCHANT_ID' }}
          />
        </Box>
      )}
      {showInternalData && (
        <TextFormField label={'FIRST_NAME'} value={merchant.registrationFields.fullName.firstName} name={'firstName'} />
      )}
      {showInternalData && (
        <TextFormField label={'LAST_NAME'} value={merchant.registrationFields.fullName.lastName} name={'lastName'} />
      )}
      {showInternalData && (
        <TextFormField label={'PHONE'} value={merchant.registrationFields.phoneNumber} name={'phoneNumber'} />
      )}
      {showInternalData && <TextFormField label={'EMAIL'} value={merchant.registrationFields.email} name={'email'} />}
    </FormGridContainer>
  );
};
